import { useMemo, useState } from 'react';
import { Avatar, Button, Card, Chip, ChipRow, EmptyState, IconTile, ListRow, Money, Stack } from '../../components';
import { categoryIcon, SearchIcon, TrashIcon } from '../../components/icons';
import { CATEGORIES } from '../../lib/categories';
import { dayKey, formatDayHeading } from '../../lib/dates';
import { formatMoney } from '../../lib/money';
import { deleteExpense } from '../../data/expenses';
import { useSubmit } from '../../hooks/useSubmit';
import { useTrip } from './TripContext';
import type { ExpenseRow } from '../../data/types';

export function ExpensesTab() {
  const { trip, expenses, myMemberId, nameOf, reload, openExpenseSheet } = useTrip();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const submit = useSubmit();

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return expenses.filter((e) => {
      if (filter !== 'all' && e.category !== filter) return false;
      if (!needle) return true;
      return (
        e.description.toLowerCase().includes(needle) ||
        nameOf(e.payer_member_id).toLowerCase().includes(needle)
      );
    });
  }, [expenses, query, filter, nameOf]);

  // Already ordered spent_at DESC by the query, so grouping preserves that.
  const days = useMemo(() => {
    const grouped = new Map<string, ExpenseRow[]>();
    for (const expense of visible) {
      const key = dayKey(expense.spent_at);
      const bucket = grouped.get(key);
      if (bucket) bucket.push(expense);
      else grouped.set(key, [expense]);
    }
    return [...grouped.values()];
  }, [visible]);

  async function handleDelete(expense: ExpenseRow) {
    const confirmed = window.confirm(
      `Delete "${expense.description}"? Everyone's balance will change.`,
    );
    if (!confirmed) return;
    if (await submit.run(() => deleteExpense(expense.id))) reload();
  }

  return (
    <Stack gap={4}>
      <h1 style={{ fontSize: 'var(--fs-display)', fontWeight: 300, letterSpacing: '-0.02em' }}>
        Expenses
      </h1>

      <Card style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <SearchIcon size={19} style={{ color: 'var(--text-muted)', flex: '0 0 auto' }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search expenses…"
            aria-label="Search expenses"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: 'var(--fs-body-lg)',
              color: 'var(--text)',
            }}
          />
        </div>
      </Card>

      <ChipRow>
        <Chip selected={filter === 'all'} onClick={() => setFilter('all')}>
          All
        </Chip>
        {CATEGORIES.map((c) => (
          <Chip key={c.key} selected={filter === c.key} onClick={() => setFilter(c.key)}>
            {c.label}
          </Chip>
        ))}
      </ChipRow>

      {submit.error && (
        <div style={{ color: 'var(--error)', fontSize: 'var(--fs-body-sm)' }}>{submit.error}</div>
      )}

      {days.length === 0 ? (
        <Card>
          <EmptyState
            title={expenses.length === 0 ? 'Nothing logged yet' : 'Nothing matches'}
            action={
              expenses.length === 0 ? (
                <Button onClick={() => openExpenseSheet()}>Add an expense</Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQuery('');
                    setFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )
            }
          >
            {expenses.length === 0
              ? 'Everything anyone adds shows up here, newest first.'
              : 'Try a different search or category.'}
          </EmptyState>
        </Card>
      ) : (
        days.map((items) => (
          <div key={dayKey(items[0].spent_at)}>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-md)', marginBottom: 'var(--sp-2)' }}>
              {formatDayHeading(items[0].spent_at)}
            </div>
            <Card flush>
              {items.map((expense, index) => (
                <ExpenseRowItem
                  key={expense.id}
                  expense={expense}
                  divider={index > 0}
                  baseCurrency={trip.base_currency}
                  payerName={nameOf(expense.payer_member_id)}
                  myMemberId={myMemberId}
                  onEdit={() => openExpenseSheet(expense)}
                  onDelete={() => void handleDelete(expense)}
                />
              ))}
            </Card>
          </div>
        ))
      )}
    </Stack>
  );
}

function ExpenseRowItem({
  expense,
  divider,
  baseCurrency,
  payerName,
  myMemberId,
  onEdit,
  onDelete,
}: {
  expense: ExpenseRow;
  divider: boolean;
  baseCurrency: string;
  payerName: string;
  myMemberId: string | null;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const CategoryIcon = categoryIcon(expense.category);
  const myShare = expense.trip_expense_splits.find((s) => s.member_id === myMemberId);
  const foreign = expense.currency !== baseCurrency;

  return (
    <ListRow divider={divider}>
      {/*
        The category tile keeps the icon, with the payer's colour as a small
        badge on it — the list is scanned for both "what was it" and "whose is
        this", and replacing one with the other loses more than it gains.
      */}
      <div style={{ position: 'relative', flex: '0 0 auto' }}>
        <IconTile size={40}>
          <CategoryIcon size={19} />
        </IconTile>
        <div style={{ position: 'absolute', right: -4, bottom: -4, borderRadius: '50%', boxShadow: '0 0 0 2px var(--surface)' }}>
          <Avatar memberId={expense.payer_member_id} displayName={payerName} size={20} />
        </div>
      </div>

      <button
        type="button"
        onClick={onEdit}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: 'var(--fs-body-lg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {expense.description}
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
          {payerName} paid
          {expense.trip_expense_splits.length > 0 && ` · split ${expense.trip_expense_splits.length} ways`}
          {myShare && ` · you ${formatMoney(myShare.base_amount, baseCurrency)}`}
        </div>
      </button>

      <div style={{ textAlign: 'right' }}>
        <Money amount={expense.base_amount} currency={baseCurrency} />
        {foreign && (
          <div className="num" style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
            {formatMoney(expense.amount, expense.currency)} {expense.currency}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${expense.description}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          color: 'var(--text-muted)',
          flex: '0 0 auto',
        }}
      >
        <TrashIcon size={17} />
      </button>
    </ListRow>
  );
}
